import React, { useState } from "react";
import { apiSetPriorityOverride } from "./api";

// PriorityOverrideModal — Super Admin sets/updates a temporary priority override
// for a department. Surfaces backend 4xx detail in an inline gold-bordered error.
export default function PriorityOverrideModal({ dept, onSaved, onClose }) {
  const existing = dept.priorityOverride || null;
  const [form, setForm] = useState({
    priority: existing?.priority || (dept.priority === "high" ? "medium" : "high"),
    startDate: existing?.startDate || new Date().toISOString().slice(0, 10),
    endDate: existing?.endDate || "",
    reason: existing?.reason || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); if (error) setError(""); };
  const submit = async () => {
    if (!form.startDate || !form.endDate) { setError("Start and end dates are required."); return; }
    if (form.endDate < form.startDate) { setError("End date must be on or after start date."); return; }
    setSaving(true);
    try {
      await apiSetPriorityOverride(dept.id, {
        priority: form.priority,
        startDate: form.startDate,
        endDate: form.endDate,
        reason: form.reason.trim(),
      });
      onSaved && onSaved();
    } catch (e) {
      setError(e.message || "Could not save override");
      setSaving(false);
    }
  };
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" data-testid="dept-override-modal" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">{existing ? "Edit Priority Override" : "Set Priority Override"}</div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ fontSize: 12, color: "var(--kc-sub)", fontFamily: "'Montserrat',sans-serif", marginBottom: 14, lineHeight: 1.5 }}>
            Temporarily change <strong style={{ color: "var(--kc-text)" }}>{dept.name}</strong>&apos;s priority for a defined period.
            The department leader and members will be notified. The original priority{" "}
            (<span className={`priority-badge ${dept.priority || "medium"}`}>{dept.priority || "medium"}</span>)
            will automatically resume after the end date.
          </div>
          <div className="form-grid mb-14">
            <div className="form-group">
              <label>New Priority *</label>
              <select data-testid="dept-override-priority" value={form.priority} onChange={e => set("priority", e.target.value)}>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
            <div className="form-group">
              <label>Start Date *</label>
              <input type="date" data-testid="dept-override-start" value={form.startDate} onChange={e => set("startDate", e.target.value)} />
            </div>
          </div>
          <div className="form-group mb-14">
            <label>End Date *</label>
            <input type="date" data-testid="dept-override-end" value={form.endDate} onChange={e => set("endDate", e.target.value)} min={form.startDate} />
          </div>
          <div className="form-group">
            <label>Reason (optional)</label>
            <textarea
              data-testid="dept-override-reason"
              value={form.reason}
              onChange={e => set("reason", e.target.value)}
              rows={2}
              placeholder="e.g. Easter Production"
            />
          </div>
          {error && (
            <div
              data-testid="dept-override-error"
              role="alert"
              style={{
                marginTop: 10,
                background: "color-mix(in srgb, var(--kc-accent) 10%, transparent)",
                border: "1px solid color-mix(in srgb, var(--kc-accent) 45%, transparent)",
                color: "var(--kc-accent)",
                borderRadius: 8, padding: "8px 12px",
                fontFamily: "'Montserrat', sans-serif", fontSize: 12, fontWeight: 600,
              }}
            >
              {error}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose} disabled={saving}>Cancel</button>
          <button
            className="btn btn-gold"
            data-testid="dept-override-save"
            disabled={saving || !form.startDate || !form.endDate}
            onClick={submit}
          >
            {saving ? "Saving…" : (existing ? "Update Override" : "Set Override")}
          </button>
        </div>
      </div>
    </div>
  );
}
